import {computed, createApp, ref} from "vue";
import {usePopup} from "@components/popup/composable.js";
import {useHead} from "@unhead/vue";
import {locationToUrl} from "@utils/utils.js";
import ErrorComponent from "@components/Error.vue";

const popup = usePopup();

export function initCustomInputState(isShowDefault = true) {
    const content = ref('');
    const error = ref(null);
    const isShow = ref(isShowDefault);

    const toggleShow = () => { isShow.value = !isShow.value; };

    const setContent = (val) => {
        content.value = val;
        error.value = null;
    };

    const setError = err => { error.value = err; };

    const reset = () => setContent('');

    return {
        content: content,
        error: error,
        isShow: isShow,
        toggleShow,
        setContent,
        setError,
        reset
    };
}

export function initToggleMenu() {
    const menuOpen = ref(false);
    let hasEventListener = false;

    const handleClickOutside = (event) => {
        const target = event.target;
        if(target.closest('#popup')) return
        menuOpen.value = false;
        removeListener();
    };

    const addListener = () => {
        if (!hasEventListener) {
            document.addEventListener('click', handleClickOutside, true);
            hasEventListener = true;
        }
    };

    const removeListener = () => {
        if (hasEventListener) {
            document.removeEventListener('click', handleClickOutside, true);
            hasEventListener = false;
        }
    };

    const toggle = (open) => {
        if (typeof open === 'boolean') {
            menuOpen.value = open;
            open ? addListener() : removeListener();
            return;
        }
        menuOpen.value = !menuOpen.value;
        menuOpen.value ? addListener() : removeListener();
    };

    // 組件銷毀時手動清理用（optional）
    const destroy = () => {
        removeListener();
    };

    return {
        menuOpen,
        toggleMenu: toggle,
        destroy
    };
}

export function initIntervalTimer() {
    const pastSec = ref(0);
    let intervalId = null;

    const start = () => {
        if (intervalId) return; // 防止重複啟動
        intervalId = setInterval(() => {
            pastSec.value++;
        }, 1000);
    };

    const stop = () => {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
    };

    const reset = () => {
        pastSec.value = 0;
        stop();
    };

    return {
        pastSec,
        start,
        stop,
        reset
    };
}

export function initStatement() {
    const getIsPortrait = () => {
        return window.innerHeight > window.innerWidth;
    };

    const getScreenType = () => {
        const width = window.innerWidth;
        if (width > 1440) return 'desktop';
        if (width > 768) return 'pad';
        return 'mobile';
    }

    const isPortrait = ref(getIsPortrait());
    const screenType = ref(getScreenType());

    const isDesktop = computed(() => screenType.value === 'desktop');
    const isPad = computed(() => screenType.value === 'pad');

    const onResize = () => {
        isPortrait.value = getIsPortrait();
        screenType.value = getScreenType();
    };

    window.addEventListener('resize', onResize);

    const destroy = () => {
        window.removeEventListener('resize', onResize);
    };

    return {
        isPortrait,
        isDesktop,
        isPad,
        destroy
    };
}

export function initHead(setting = {}) {
    const commonSettings = {
        meta: [
            { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ],
        link: [
            { rel: 'icon', type: 'image/png', href: '/favicon.png' }
        ],
        script: [
            {
                src: 'https://www.googletagmanager.com/gtag/js?id=G-KTB5EMGGXJ',
                async: true
            },
            {
                innerHTML: `
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', 'G-KTB5EMGGXJ');
                `
            },
        ]
    };
    useHead({
        ...commonSettings,
        ...setting
    });
}

export function loadingWrapper(fn, delay = 2000) {
    return async (...args) => {
        let loading = null;
        let timeout = null;
        try {
            timeout = setTimeout(() => {
                popup.show('loading').then(l => loading = l);
            }, delay);
            return await fn(...args);
        } catch (error) {
            throw error;
        } finally {
            if (loading) loading.close();
            if (timeout) clearTimeout(timeout);
        }
    };
}

export function commonErrorHandler(error) {
    let errorMessage = '發生未知錯誤';
    try {
        console.error(error);
        const errorData = error?.data;
        if (errorData?.redirectUrl) {
            alert(errorData.error || errorMessage);
            locationToUrl(errorData.redirectUrl);
            return;
        }
        errorMessage = errorData?.error || errorMessage;
    } catch (e) {
        errorMessage = e || errorMessage;
    }
    createApp(ErrorComponent, { errorMessage }).mount('#app');
}


